// Page: SignIn
// Purpose: Supports email/password and Google sign-in, manages error flags,
// Stripe checkout recovery, and navigates based on plan level and context.
import React, { useState, useEffect, useRef } from 'react';
import { auth, googleAuthProvider } from '../../../firebase/firebase';
import { signInWithPopup, setPersistence, browserLocalPersistence } from 'firebase/auth';
import SocialSignIn from '../../../components/auth/signin/SocialSignIn';
import SignInForm from '../../../components/auth/signin/SignInForm';
import { useAuth } from '../../../context/auth';
import { SimpleLandingHeader } from '@/components/landing/shared/SimpleLandingHeader/SimpleLandingHeader';
import useDarkMode from '../../../hooks/useDarkMode';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import AuthLoadingScreen from '../../../components/shared/AuthLoadingScreen';
import { clearAllAuthErrors } from '../../../utils/errors/authErrorUtils';
import { EnterpriseDomainModal } from '../../../components/ui/modals/enterprise-domain-modal.jsx';
import api from '../../../api';
import SEOHead from '../../../components/shared/SEOHead';
import Footer from '../../../components/shared/Footer';

// Storage key for persisting auth errors
const AUTH_ERROR_STORAGE_KEY = 'refrral_auth_error';
// Storage key for tracking Google sign-in attempts
const GOOGLE_SIGNIN_IN_PROGRESS_KEY = 'googleSignInInProgress';
// Storage key for auth redirects
const AUTH_REDIRECT_STATE_KEY = 'auth_redirect_state';
// Storage keys for Stripe checkout restoration
const STRIPE_REDIRECT_AUTH_UID = 'stripeRedirectAuthUID';
const STRIPE_SESSION_ID = 'stripeSessionId';

export default function SignIn() {
  // Check for any stored errors from previous operations with timestamp validation
  const getInitialError = () => {
    const storedError = localStorage.getItem(AUTH_ERROR_STORAGE_KEY) || 
                       localStorage.getItem('refrral_auth_error');
    
    if (storedError) {
      // Check if this is a recent error (within last 5 minutes)
      const errorTimestamp = localStorage.getItem('auth_error_timestamp');
      const isRecentError = errorTimestamp && (Date.now() - parseInt(errorTimestamp)) < 300000; // 5 minutes
      
      if (isRecentError) {
        console.log('Found recent error on component initialization:', storedError);
        return storedError;
      } else {
        console.log('Found stale error on component initialization, ignoring:', storedError);
      }
    }
    
    return null;
  };
  
  const [error, setError] = useState(getInitialError());
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [comingFromStripe, setComingFromStripe] = useState(false);
  const [stripeSessionId, setStripeSessionId] = useState(null);
  const { signIn, signInWithGoogle, resetTransitioning, isAuthenticating, isTransitioning, user } = useAuth();
  const isDark = useDarkMode();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Add ref to track if we've already processed Stripe checkout detection
  const stripeCheckoutProcessed = useRef(false);
  const [showEnterpriseDomainModal, setShowEnterpriseDomainModal] = useState(false);
  const [enterpriseDomainLoading, setEnterpriseDomainLoading] = useState(false);

  // Clear stale errors when component mounts (unless coming from specific error scenarios)
  useEffect(() => {
    // Check if we're coming from a legitimate error scenario
    const isFromStripeError = location.state?.from === 'stripe-success' && location.state?.message;
    const isFromCheckoutError = location.search.includes('session_id=') || 
                               localStorage.getItem('stripe_checkout_initiated') === 'true';
    
    // Check if we're on a fresh page load (no referrer or coming from external)
    const isFreshPageLoad = !document.referrer || 
                           !document.referrer.includes(window.location.origin) ||
                           performance.navigation?.type === 1; // TYPE_RELOAD
    
    // IMPORTANT: Don't aggressively clear errors on fresh page loads anymore
    // Let the second useEffect handle legitimate error checking
    // Only clear errors in very specific scenarios to avoid conflicts
    
    // If user is already authenticated, clear any errors and redirect
    if (user) {
      console.log('SignIn: User already authenticated, checking for errors before redirecting');
      
      // CRITICAL FIX: Clear Google sign-in error flag immediately when user is authenticated
      // This prevents authenticated users from getting stuck on sign-in page due to stale error flags
      const hadGoogleSignInError = sessionStorage.getItem('google_signin_error') === 'true';
      if (hadGoogleSignInError) {
        console.log('SignIn: Clearing stale Google sign-in error flag for authenticated user');
        sessionStorage.removeItem('google_signin_error');
        sessionStorage.removeItem('google_signin_error_timestamp');
      }
      
      // Check for other auth errors (but not Google sign-in errors since we just cleared them)
      const hasAuthError = localStorage.getItem('refrral_auth_error') !== null ||
                          localStorage.getItem('auth_error_timestamp') !== null;
      
      if (hasAuthError) {
        console.log('SignIn: Auth error detected, staying on sign-in page');
        return;
      }
      
      // Use the utility function to clear all auth errors
      clearAllAuthErrors();
      setError(null);
      
      // Redirect based on user's plan level
      const isPremiumOrEnterprise = user.planLevel === 'premium' || user.isEnterprise;
      if (isPremiumOrEnterprise) {
        navigate('/app/', { replace: true });
      } else {
        navigate('/pricing', { replace: true });
      }
      return;
    }
    
    // REMOVED: Don't add event listeners that aggressively clear errors
    // This was causing issues with error display
    
  }, [user, navigate, location]); // Simplified dependencies
  
  // Update the Stripe checkout detection logic:
  useEffect(() => {
    // Only run this once per component mount
    if (stripeCheckoutProcessed.current) {
      return;
    }
    
    // Check for Stripe session ID in URL query params
    const searchParams = new URLSearchParams(location.search);
    const sessionIdParam = searchParams.get('session_id');
    
    // Check for Stripe session ID in location state (if navigated programmatically)
    const stateSessionId = location.state?.sessionId;
    
    // Get any stored session ID from localStorage
    const storedSessionId = localStorage.getItem(STRIPE_SESSION_ID);
    
    // Check if we're returning from Stripe checkout via state
    const storedCheckoutState = localStorage.getItem('stripe_checkout_state');
    let checkoutInProgress = false;
    
    if (storedCheckoutState) {
      try {
        const checkoutData = JSON.parse(storedCheckoutState);
        if (checkoutData.inProgress) {
          checkoutInProgress = true;
        }
      } catch (e) {
        console.error('Error parsing stored checkout state:', e);
      }
    }
    
    // Determine if we're coming from Stripe
    const isFromStripe = 
      sessionIdParam || 
      stateSessionId || 
      storedSessionId ||
      checkoutInProgress;
    
    // CRITICAL FIX: Only consider this a valid Stripe checkout if we have both session data AND a user
    // If we have session data but no user, it's stale data from previous logout/deletion
    const isValidStripeCheckout = isFromStripe && user;
    const isStaleStripeData = isFromStripe && !user;
    
    if (isValidStripeCheckout) {
      console.log('User already authenticated and coming from Stripe checkout, redirecting...');
      
      // Clear the message since we're already logged in
      setError(null);
      setComingFromStripe(false);
      
      // If we have a session ID, go to success page; otherwise, go to pricing
      if (sessionIdParam) {
        navigate(`/success?session_id=${sessionIdParam}`, { replace: true });
      } else if (stateSessionId) {
        navigate(`/success?session_id=${stateSessionId}`, { replace: true });
      } else if (storedSessionId) {
        navigate(`/success?session_id=${storedSessionId}`, { replace: true });
      } else {
        navigate('/pricing', { replace: true });
      }
      stripeCheckoutProcessed.current = true;
      return;
    }
    
    // CRITICAL FIX: If we have Stripe session data but NO USER, this means it's stale data from a previous logout/deletion
    // Clear the stale data to prevent incorrect redirects
    if (isStaleStripeData) {
      console.log('Found stale Stripe session data without authenticated user - clearing it');
      localStorage.removeItem('stripeSessionId');
      localStorage.removeItem('stripe_session_id');
      localStorage.removeItem('stripe_checkout_initiated');
      localStorage.removeItem('stripe_checkout_auth_state');
      localStorage.removeItem('stripe_checkout_return_path');
      localStorage.removeItem('post_login_redirect');
      localStorage.removeItem('post_login_session_id');
      sessionStorage.removeItem('in_stripe_checkout_flow');
      sessionStorage.removeItem('stripe_checkout_cancelled');
      console.log('Cleared stale Stripe session data');
      // Don't set comingFromStripe to true for stale data
      stripeCheckoutProcessed.current = true;
      return;
    }
    
    if (isFromStripe) {
      console.log('Detected user coming from Stripe checkout, but needs login');
      setComingFromStripe(true);
      
      // Get the session ID from any available source
      const effectiveSessionId = sessionIdParam || stateSessionId || storedSessionId;
      if (effectiveSessionId) {
        setStripeSessionId(effectiveSessionId);
        console.log('Found Stripe session ID:', effectiveSessionId);
      }
      
      // If we have a message from the redirect, show it as an error/info message
      if (location.state?.message) {
        setAuthError(location.state.message);
      } else {
        // Don't set a generic message - let the user sign in normally
        console.log('Coming from Stripe checkout but no specific message provided');
      }
    }
    
    // Mark as processed to prevent running again
    stripeCheckoutProcessed.current = true;
  }, []); // Empty dependency array - only run once on mount
  
  // Separate effect just for displaying stored errors on mount - runs only once
  useEffect(() => {
    // Only run this on the initial mount
    console.log('SignIn: Checking for stored errors on initial mount');
    
    const storedError = localStorage.getItem(AUTH_ERROR_STORAGE_KEY);
    const authError = localStorage.getItem('refrral_auth_error');
    
    if (storedError || authError) {
      const errorToCheck = storedError || authError;
      console.log('Found stored error on initial mount:', errorToCheck);
      
      // Check if this is a recent error (within last 5 minutes)
      const errorTimestamp = localStorage.getItem('auth_error_timestamp');
      const isRecentError = errorTimestamp && (Date.now() - parseInt(errorTimestamp)) < 300000; // 5 minutes
      
      if (isRecentError) {
        console.log('Displaying recent error on mount:', errorToCheck);
        setError(errorToCheck);
        
        // Clean up duplicate storage
        if (authError && !storedError) {
          localStorage.setItem(AUTH_ERROR_STORAGE_KEY, authError);
          localStorage.removeItem('refrral_auth_error');
        }
      } else {
        console.log('Found stale error on mount, clearing it:', errorToCheck);
        // Clear stale errors
        clearAllAuthErrors();
      }
    }
  }, []); // Empty dependency array - run only once on mount
  
  // Check for errors in localStorage on mount and when auth state changes
  useEffect(() => {
    console.log('SignIn useEffect running - auth state changed:', { 
      resetTransitioning: !!resetTransitioning, 
      isAuthenticating, 
      isTransitioning,
      currentError: error
    });
    
    // This effect now only handles cleanup and auth state management
    // Error display is handled by the separate effect above
    
    // Always clear Google sign-in error flag
    sessionStorage.removeItem('google_signin_error');
    
    // Clean up flags on component unmount
    return () => {
      console.log('SignIn useEffect cleanup running - checking if should preserve errors');
      
      // IMPORTANT: Don't clear errors if we just had an authentication error
      // Check if there's a recent error that should be preserved
      const recentError = localStorage.getItem(AUTH_ERROR_STORAGE_KEY);
      const errorTimestamp = localStorage.getItem('auth_error_timestamp');
      const hasRecentError = recentError && errorTimestamp && 
                            (Date.now() - parseInt(errorTimestamp)) < 30000; // 30 seconds
      
      if (hasRecentError) {
        console.log('SignIn: Preserving recent auth error on cleanup:', recentError);
        // Don't clear errors if there's a recent authentication error
        sessionStorage.removeItem(GOOGLE_SIGNIN_IN_PROGRESS_KEY);
        return;
      }
      
      // Only clear errors when navigating away from sign-in page (unless going to success page)
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith('/success') && !currentPath.startsWith('/cancel')) {
        console.log('SignIn: Clearing auth errors on navigation away from sign-in page');
        
        // Use the utility function to clear all auth errors
        clearAllAuthErrors();
        
        // Reset auth state when navigating away
        resetTransitioning();
      }
      
      // Keep errors in localStorage for persistence, but clear flags
      sessionStorage.removeItem(GOOGLE_SIGNIN_IN_PROGRESS_KEY);
    };
  }, [resetTransitioning]); // Only depend on resetTransitioning to avoid re-running too often
  
  // Check if user is coming from an aborted Stripe checkout
  useEffect(() => {
    const checkStripeCheckoutReturn = async () => {
      try {
        // Check if we were in the Stripe checkout flow
        const wasInCheckoutFlow = localStorage.getItem('stripe_checkout_initiated') === 'true';
        const storedAuthState = localStorage.getItem('stripe_checkout_auth_state');
        const sessionId = localStorage.getItem('stripeSessionId');
        const returnPath = localStorage.getItem('stripe_checkout_return_path');
        
        if (wasInCheckoutFlow && storedAuthState) {
          console.log('Detected user coming from aborted Stripe checkout to login page');
          
          // Parse the stored auth state
          const authState = JSON.parse(storedAuthState);
          
          // Set an explanatory error message for the user
          setAuthError('Your session expired after leaving the payment page. Please sign in to continue.');
          
          // Store return information for after login
          if (returnPath) {
            localStorage.setItem('post_login_redirect', returnPath);
          }
          
          // Preserve the session ID for after login to potentially continue checkout
          if (sessionId) {
            localStorage.setItem('post_login_session_id', sessionId);
          }
        }
      } catch (error) {
        console.error('Error checking Stripe checkout return in login page:', error);
      }
    };
    
    // Run this check when the component mounts
    checkStripeCheckoutReturn();
  }, []);
  
  // Special handler for after login when coming from aborted Stripe checkout
  const handleStripeCheckoutReturn = () => {
    // Check if we should redirect to a specific page after login
    const returnPath = localStorage.getItem('post_login_redirect');
    const sessionId = localStorage.getItem('post_login_session_id');
    
    // Clear these flags as we're handling them now
    localStorage.removeItem('post_login_redirect');
    localStorage.removeItem('post_login_session_id');
    localStorage.removeItem('stripe_checkout_initiated');
    localStorage.removeItem('stripe_checkout_auth_state');
    localStorage.removeItem('stripe_checkout_return_path');
    
    if (returnPath) {
      console.log('Redirecting to saved return path after login:', returnPath);
      navigate(returnPath);
      return true;
    }
    
    return false;
  };

  // Helper function to set error with localStorage persistence
  const setAuthError = (errorMessage) => {
    if (errorMessage) {
      console.log('Setting auth error:', errorMessage);
      localStorage.setItem(AUTH_ERROR_STORAGE_KEY, errorMessage);
      // IMPORTANT: Set timestamp so error is considered "recent" and will be displayed
      localStorage.setItem('auth_error_timestamp', Date.now().toString());
      setError(errorMessage);
    } else {
      console.log('Clearing auth error from all storage locations');
      // Clear ALL possible auth error storage locations
      const authErrorKeys = [
        AUTH_ERROR_STORAGE_KEY, // 'refrral_auth_error'
        'refrral_auth_error',
        'auth_error', 
        'stripe_auth_error',
        'refrral_auth_error_key',
        'auth_error_storage_key', // This one from CheckoutSuccess.jsx
        'auth_error_timestamp' // Also clear the timestamp
      ];
      
      authErrorKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        } catch (e) {
          console.error(`Error clearing auth error key ${key}:`, e);
        }
      });
      
      setError(null);
      console.log('All auth error storage locations cleared');
    }
  };

  const toggleTheme = () => {
    if (isDark) {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  };

  // Clear error when user starts typing
  const handleInputChange = () => {
    console.log('SignIn: User interacted with form, clearing all auth errors from all storage locations');
    
    // Always clear ALL possible auth error storage locations, regardless of current error state
    const authErrorKeys = [
      AUTH_ERROR_STORAGE_KEY, // 'refrral_auth_error'
      'refrral_auth_error',
      'auth_error', 
      'stripe_auth_error',
      'refrral_auth_error_key',
      'auth_error_storage_key', // This one from CheckoutSuccess.jsx
      'auth_error_timestamp', // Also clear the timestamp
      // Stripe-related keys that might contain error messages
      'stripe_checkout_initiated',
      'stripe_checkout_auth_state',
      'stripe_checkout_return_path',
      'post_login_redirect',
      'post_login_session_id'
    ];
    
    authErrorKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      } catch (e) {
        console.error(`Error clearing auth error key ${key}:`, e);
      }
    });
    
    // Clear Google sign-in error flag to allow navigation again
    sessionStorage.removeItem('google_signin_error');
    
    // Clear the component state error
    setError(null);
    
    console.log('SignIn: All auth error storage locations and Stripe flags cleared');
  };
  
  // Clear error when user navigates away (for header links)
  const handleNavigation = () => {
    console.log('SignIn: Clearing auth errors on navigation');
    
    // Use the utility function to clear all auth errors
    clearAllAuthErrors();
    
    // Clear Google sign-in error flag when navigating away intentionally
    sessionStorage.removeItem('google_signin_error');
    
    setError(null);
  };

  // Special handler for Stripe checkout flow
  const continueToStripeSuccess = async () => {
    console.log('Continuing to Stripe success page with session ID:', stripeSessionId);
    
    // OPTIMIZED: Let the API layer handle token refresh when needed
    // No need to force refresh here as tokens will be refreshed if needed during API calls
    
    // If we have a session ID, navigate to the checkout success page
    if (stripeSessionId) {
      navigate(`/success?session_id=${stripeSessionId}`, { replace: true });
    } else {
      // If no session ID, just go to the app or pricing as usual
      navigate('/pricing', { replace: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAuthError(null);
    setIsLoading(true);
    
    const formData = new FormData(e.target);
    const email = formData.get('email');
    const password = formData.get('password');
    
    try {
      // Clear any stale checkout data if this is a normal sign-in (not from checkout)
      if (!comingFromStripe && !stripeSessionId) {
        console.log('Normal sign-in detected, clearing any stale checkout data');
        localStorage.removeItem('stripe_auth_uid');
        localStorage.removeItem('stripe_auth_email');
        localStorage.removeItem('stripe_auth_timestamp');
        localStorage.removeItem('stripeSessionId');
        localStorage.removeItem('stripeSessionCreatedAt');
        sessionStorage.removeItem('checkout_auth_uid');
        sessionStorage.removeItem('checkout_auth_email');
        localStorage.removeItem('checkout_auth_timestamp');
        localStorage.removeItem('auth_for_checkout');
        localStorage.removeItem('stripe_checkout_auth_state');
        localStorage.removeItem('stripe_checkout_return_path');
        sessionStorage.removeItem('in_stripe_checkout_flow');
        localStorage.removeItem('stripe_checkout_initiated');
      }
      
      // Use the signIn function from auth context
      const user = await signIn(email, password);
      
      // Successfully signed in
      console.log('Sign-in successful via form, preparing for redirect...', {
        user: user ? {
          uid: user.uid,
          planLevel: user.planLevel,
          email: user.email,
          subscriptionStatus: user?.subscriptionStatus
        } : 'No user data'
      });
      
      // CRITICAL: Clear any Google sign-in error flags that might prevent proper navigation
      sessionStorage.removeItem('google_signin_error');
      sessionStorage.removeItem('google_signin_error_timestamp');
      
      // Check if we were coming from Stripe checkout and handle accordingly
      if (handleStripeCheckoutReturn()) {
        return; // The function handled the redirect
      }
      
      // If we're coming from Stripe checkout, redirect back to pricing page
      if (comingFromStripe) {
        await continueToStripeSuccess();
        return;
      }
      
      // Sign-in successful - RequireUnauth component will handle the loading screen and redirect
      console.log('Sign-in process complete, RequireUnauth will handle navigation');
      
    } catch (err) {
      console.error('Sign in error:', err);
      let errorMessage = 'Failed to sign in';
      
      // Handle authentication errors by checking both error code and message
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential' || 
          (err.message && err.message.includes('Invalid email or password'))) {
        errorMessage = 'Invalid email or password';
      } else if (err.code === 'auth/too-many-requests' || 
                (err.message && err.message.includes('Too many failed attempts'))) {
        errorMessage = 'Too many failed attempts. Please try again later';
      } 
      // Handle profile loading errors
      else if (err.code === 'auth/profile-not-found' || err.status === 404) {
        errorMessage = 'Account exists but profile is missing. Please contact support.';
      } else if (err.message && err.message.includes('profile')) {
        errorMessage = 'Error loading your profile. Please try again.';
      }
      // If we have a user-friendly error message from the auth service, use it
      else if (err.message && !err.message.includes('Failed to sign in')) {
        errorMessage = err.message;
      }
      
      // Set the error message
      setAuthError(errorMessage);
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    // CRITICAL FIX: Clear all auth errors at the start to prevent blocking
    setAuthError(null);
    localStorage.removeItem('refrral_auth_error');
    sessionStorage.removeItem('google_signin_error');
    console.log('Cleared all auth errors before Google sign-in attempt');
    
    // IMPORTANT: Don't set setGoogleLoading(true) immediately to avoid flashes
    // Only set it when we're about to show the popup
    
    // Set Google sign-in flag to track state
    sessionStorage.setItem('googleSignInInProgress', 'true');
    
    // Clear any leftover flags from failed sign-ins
    sessionStorage.removeItem('justLoggedIn');
    
    try {
      // Check if auth is initialized
      if (!auth) {
        throw new Error('Authentication service unavailable. Please try again later.');
      }
      
      // OPTIMIZED: Ensure Firebase persistence is set to LOCAL before sign-in attempt
      try {
        await setPersistence(auth, browserLocalPersistence);
        console.log('Firebase persistence set to LOCAL before Google sign-in');
      } catch (persistenceError) {
        console.warn('Failed to set persistence before Google sign-in:', persistenceError);
        // Continue with sign-in anyway
      }
      
      // NOW set loading state right before showing popup
      setGoogleLoading(true);
      console.log('Google sign-in: Setting loading state and showing popup');
      
      // Get user info with Google sign-in
      let result, firebaseUser;
      
      try {
        // Try popup first
        result = await signInWithPopup(auth, googleAuthProvider);
        firebaseUser = result.user;
      } catch (popupError) {
        // Clear loading on popup error
        setGoogleLoading(false);
        
        // Check if popup was blocked
        if (popupError.code === 'auth/popup-blocked' || 
            popupError.code === 'auth/popup-closed-by-user' ||
            popupError.message?.includes('popup')) {
          console.log('Popup was blocked, falling back to redirect method');
          
          // Import signInWithRedirect as fallback
          const { signInWithRedirect } = await import('firebase/auth');
          await signInWithRedirect(auth, googleAuthProvider);
          return; // Exit here, redirect will handle the rest
        }
        
        // If it's not a popup issue, rethrow the error
        throw popupError;
      }
      
      // Popup completed successfully, now clear loading temporarily while processing
      setGoogleLoading(false);
      console.log('Google popup completed, processing authentication...');
      
      // OPTIMIZED: After successful popup, just verify we have a token, don't force refresh
      try {
        await setPersistence(auth, browserLocalPersistence);
        console.log('Firebase persistence re-confirmed after Google popup');
        
        // Just verify we can get a token, the auth functions will handle fresh tokens when needed
        const token = await firebaseUser.getIdToken(false);
        console.log('Verified Firebase token after Google sign-in, length:', token?.length);
      } catch (tokenError) {
        console.error('Error verifying token after Google sign-in:', tokenError);
        // Don't fail the sign-in, but warn about potential issues
      }
      
      // Get display name from Google account
      const displayName = firebaseUser.displayName || '';
      let firstName = '';
      let lastName = '';
      
      // Parse the display name if it exists
      if (displayName) {
        const nameParts = displayName.split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }
      
      try {
        // Clear any stale checkout data if this is a normal sign-in (not from checkout)
        if (!comingFromStripe && !stripeSessionId) {
          console.log('Normal Google sign-in detected, clearing any stale checkout data');
          localStorage.removeItem('stripe_auth_uid');
          localStorage.removeItem('stripe_auth_email');
          localStorage.removeItem('stripe_auth_timestamp');
          localStorage.removeItem('stripeSessionId');
          localStorage.removeItem('stripeSessionCreatedAt');
          sessionStorage.removeItem('checkout_auth_uid');
          sessionStorage.removeItem('checkout_auth_email');
          localStorage.removeItem('checkout_auth_timestamp');
          localStorage.removeItem('auth_for_checkout');
          localStorage.removeItem('stripe_checkout_auth_state');
          localStorage.removeItem('stripe_checkout_return_path');
          sessionStorage.removeItem('in_stripe_checkout_flow');
          localStorage.removeItem('stripe_checkout_initiated');
        }
        
        // Use the signInWithGoogle function from auth context
        const user = await signInWithGoogle(firebaseUser, firstName, lastName);
        
        // Successfully signed in with Google
        console.log('Sign-in successful via Google, navigation will be handled by NavigationHandler', {
          user: user ? {
            uid: user.uid,
            planLevel: user.planLevel,
            email: user.email,
            subscriptionStatus: user?.subscriptionStatus
          } : 'No user data'
        });
        
        // CRITICAL: Store additional auth recovery data in localStorage (like regular signIn does)
        try {
          const authRecoveryData = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            provider: 'google.com',
            timestamp: Date.now(),
            firstName,
            lastName
          };
          localStorage.setItem('auth_recovery_data', JSON.stringify(authRecoveryData));
          localStorage.setItem('google_signin_timestamp', Date.now().toString());
          sessionStorage.setItem('auth_method', 'google');
          console.log('Google sign-in auth recovery data saved to localStorage');
        } catch (storageError) {
          console.warn('Failed to save Google auth recovery data:', storageError);
        }
        
        // Don't clear the flag here - it will be cleared by AuthContext.signInWithGoogle
        // after the profile is ready. This prevents race conditions.
        
        // If we're coming from Stripe checkout, redirect back to success page
        if (comingFromStripe) {
          await continueToStripeSuccess();
          return;
        }
        
        // Sign-in successful - NavigationHandler will handle the smooth navigation
        // Don't call any navigation functions here, let the system handle it
        console.log('Google sign-in complete, letting NavigationHandler manage the redirect');
        
      } catch (profileError) {
        // Handle profile errors specifically
        console.error('Error with profile during Google sign-in:', profileError);
        
        // Stop loading and clear flags
        setGoogleLoading(false);
        
        // Clear Google sign-in flag to unblock loading page
        sessionStorage.removeItem('googleSignInInProgress');
        
        // CRITICAL: Trigger auth state refresh to clear any stuck state
        if (auth.currentUser) {
          setTimeout(() => {
            auth.currentUser.getIdToken(true).catch(console.error);
          }, 100);
        }
        
        // Log the server response for debugging
        if (profileError.response) {
          console.log('Server response:', {
            status: profileError.response.status,
            data: profileError.response.data
          });
        }
        
        // Handle specific error cases when profile creation fails
        if (profileError.code === 'auth/email-already-in-use' || 
            profileError.errorCode === 'EMAIL_ALREADY_EXISTS' ||
            profileError.errorCode === 'PROFILE_ALREADY_EXISTS' ||
            (profileError.message && profileError.message.includes('An account with this email address already exists')) ||
            (profileError.message && profileError.message.includes('email is already registered')) ||
            (profileError.message && profileError.message.includes('user profile for this account already exists'))) {
          console.log('Email already exists error during Google sign-in profile creation');
          const errorMsg = 'This email already has an account with password. Please sign in with your email and password instead of using Google.';
          setAuthError(errorMsg);
          
          // Set a flag to prevent any redirects after this error
          sessionStorage.setItem('google_signin_error', 'true');
          sessionStorage.setItem('google_signin_error_timestamp', Date.now().toString());
          
          // Sign out from Firebase to clean up auth state
          try {
            await auth.signOut();
            console.log('Signed out of Firebase after email already exists error');
          } catch (signOutError) {
            console.error('Error signing out after email already exists error:', signOutError);
          }
          
          // Explicitly navigate back to sign-in page
          console.log('Explicitly navigating back to sign-in page after Google sign-in email exists error');
          navigate('/signin', { replace: true });
          
          return;
        }
        
        // Handle 404 errors or user-not-found errors (this shouldn't happen anymore since we auto-create)
        if ((profileError.response && profileError.response.status === 404) || 
            profileError.code === 'auth/user-not-found' ||
            (profileError.message && profileError.message.includes('No account exists'))) {
          console.log('Unexpected profile not found error - should have been auto-created');
          const errorMsg = 'Error creating your account. Please try again or sign up manually.';
          setAuthError(errorMsg);
          
          // Set a flag to prevent any redirects after this error
          sessionStorage.setItem('google_signin_error', 'true');
          sessionStorage.setItem('google_signin_error_timestamp', Date.now().toString());
          
          // Sign out from Firebase to clean up auth state
          try {
            await auth.signOut();
            console.log('Signed out of Firebase after unexpected profile not found');
          } catch (signOutError) {
            console.error('Error signing out after profile not found:', signOutError);
          }
          
          // Explicitly navigate back to sign-in page
          console.log('Explicitly navigating back to sign-in page after Google sign-in profile error');
          navigate('/signin', { replace: true });
          
          return;
        }
        
        // Handle other profile errors
        const errorMsg = profileError.message || 'Failed to create your account. Please try again.';
        setAuthError(errorMsg);
        return;
      }
    } catch (err) {
      console.error('Google sign-in error:', err);
      
      // Clear Google sign-in flag to unblock loading page
      sessionStorage.removeItem('googleSignInInProgress');
      
      // IMPORTANT: Detect when user cancels the popup or other auth errors occur
      // Specific error code for popup close
      if (err.code === 'auth/popup-closed-by-user' || 
          err.code === 'auth/cancelled-popup-request' ||
          err.code === 'auth/popup-blocked') {
        console.log('User cancelled Google sign-in popup');
        setGoogleLoading(false);
        return; // Just stop loading, don't show an error
      }
      
      // Handle Firebase initialization errors
      if (err.code === 'app/no-app' || err.message?.includes('initializeApp()')) {
        setAuthError('Authentication service is initializing. Please try again in a moment.');
        setGoogleLoading(false);
        return;
      }
      
      // Always set the loading to false for any other error
      setGoogleLoading(false);
      
      // Debugging: Log the full error object to understand what's coming through
      console.log('Error details:', {
        message: err.message,
        code: err.code,
        name: err.name,
        status: err.status,
        response: err.response ? {
          status: err.response.status,
          data: err.response.data
        } : 'No response data',
        stack: err.stack ? err.stack.slice(0, 200) : 'No stack'
      });
      
      let errorMsg = '';
      // Handle specific error types
      if (err.code === 'auth/account-exists-with-different-credential' || 
          (err.message && err.message.includes('already associated with an email/password account'))) {
        console.log('Setting credential error');
        errorMsg = 'This email already has an account with password. Please sign in with your email and password instead of using Google.';
      } else if (err.code === 'auth/user-not-found' || 
                (err.message && err.message.includes('No account exists with this'))) {
        // This error should be rare now since we auto-create accounts
        console.log('Unexpected user-not-found error');
        errorMsg = 'Error processing your Google sign-in. Please try again.';
        // Set flag to prevent redirects
        sessionStorage.setItem('google_signin_error', 'true');
        sessionStorage.setItem('google_signin_error_timestamp', Date.now().toString());
        
        // Explicitly navigate back to sign-in page
        console.log('Explicitly navigating back to sign-in page after Google sign-in error');
        navigate('/signin', { replace: true });
        
      } else if (err.code === 'auth/profile-not-found' || err.status === 404 || 
                (err.response && err.response.status === 404)) {
        // This should not happen anymore since we auto-create profiles
        errorMsg = 'Error processing your Google sign-in. Please try again.';
        // Set flag to prevent redirects  
        sessionStorage.setItem('google_signin_error', 'true');
        sessionStorage.setItem('google_signin_error_timestamp', Date.now().toString());
        
        // Explicitly navigate back to sign-in page
        console.log('Explicitly navigating back to sign-in page after Google sign-in error');
        navigate('/signin', { replace: true });
      } else if (err.message && err.message.includes('profile')) {
        // Generic profile loading error
        errorMsg = 'Error loading your profile. Please try again.';
      } else if (err.message) {
        // For any other errors with a message
        console.log('Setting generic error message');
        errorMsg = `Failed to sign in with Google: ${err.message}`;
      } else {
        // Generic error fallback
        console.log('Setting fallback error message');
        errorMsg = 'Failed to sign in with Google. Please try again.';
      }
      
      console.log('Setting error for Google sign-in:', errorMsg);
      setAuthError(errorMsg);
    }
  };

  const handleEnterpriseSignin = () => {
    setShowEnterpriseDomainModal(true);
  };

  const handleEnterpriseDomainSubmit = async (domain) => {
    setEnterpriseDomainLoading(true);
    try {
      // Check if enterprise exists for this domain
      const response = await api.get(`/enterprise/check-domain/${domain}`);
      
      console.log('Enterprise domain check response:', response);
      
      // Handle different response structures
      const responseData = response.data || response;
      const exists = responseData.exists;
      const enterprise = responseData.enterprise;
      
      if (exists && enterprise) {
        // Enterprise exists, navigate to enterprise signin page
        navigate('/enterprise/signin', { 
          state: { 
            enterprise: enterprise,
            domain 
          } 
        });
      } else {
        // No enterprise found for this domain
        setError('No enterprise plan found for this domain. Please contact your administrator or sign in with a regular account.');
        setShowEnterpriseDomainModal(false);
      }
    } catch (err) {
      console.error('Error checking enterprise domain:', err);
      
      // Handle 404 responses
      if (err.status === 404 || (err.response && err.response.status === 404)) {
        setError('No enterprise plan found for this domain. Please contact your administrator or sign in with a regular account.');
      } else {
        setError('Error checking enterprise domain. Please try again.');
      }
      setShowEnterpriseDomainModal(false);
    } finally {
      setEnterpriseDomainLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
      <SEOHead pageType="signin" />
      
      {/* Use the SimpleLandingHeader component */}
      <SimpleLandingHeader 
        isDark={isDark} 
        toggleTheme={toggleTheme} 
        backLink="/"
        showLogo={true}
        onNavigation={handleNavigation}
      />
      
      {/* Content section */}
      <div className="bg-white dark:bg-gray-900 px-4 py-12 w-full">
        <div className="sm:mx-auto sm:w-full sm:max-w-md mb-8">
          <div className="text-center">
            <h2 className="text-base font-semibold leading-7 text-purple-600 dark:text-purple-400">Welcome Back</h2>
            <p className="mt-2 text-4xl font-semibold tracking-tight leading-tight text-balance text-gray-900 dark:text-white sm:text-5xl sm:leading-tight max-w-[90%] md:max-w-full mx-auto">
              Let's get you back in
            </p>
            <p className="mt-4 text-lg text-gray-700 dark:text-gray-300 max-w-[90%] md:max-w-full mx-auto">
              Continue creating high-impact content for your campaigns.
            </p>
          </div>
        </div>
        
        {/* Simple in-page error message */}
        {error && (
          <div className="mx-auto mb-6 w-[95%] md:w-[85%] max-w-[600px] rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700/50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Email/Password Sign In Form */}
        <SignInForm 
          onSubmit={handleSubmit} 
          isLoading={isLoading} 
          onInputChange={handleInputChange}
        />
        
        {/* Google Sign In */}
        <SocialSignIn 
          onGoogleSignIn={handleGoogleSignIn} 
          isLoading={googleLoading}
          onInputChange={handleInputChange}
          onEnterpriseSignIn={handleEnterpriseSignin}
        />
      </div>
      
      {/* Enterprise Domain Modal */}
      <EnterpriseDomainModal
        isOpen={showEnterpriseDomainModal}
        onClose={() => setShowEnterpriseDomainModal(false)}
        onContinue={handleEnterpriseDomainSubmit}
        isLoading={enterpriseDomainLoading}
        actionType="signin"
      />
      
      <Footer />
    </div>
  );
}